import * as React from "react";

import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { IcChevronDownThin } from "@racoon/icons";
import { getEnumName } from "@racoon/lib";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Input,
} from "@racoon/ui";

import { incidentNoteLabel, type JobIncidentNote } from "~/shared/lib/job-incident";
import { IncidentChip } from "~/shared/ui/incident-chip";
import {
  EXPLANATION_MAX_LENGTH,
  incidentOptions,
  type StagedIncident,
} from "../model/incident-correction";

const { EXPIRED, OTHER } = ConnectProto.DriverIncidentType;

type Props = {
  /** What the job's Инцидент reads now; undefined while its incidents load. */
  current?: JobIncidentNote;
  staged?: StagedIncident;
  onStage: (incident: StagedIncident) => void;
  /** «Не успели (долг)» is offered too. */
  notCompleted: boolean;
  disabled?: boolean;
};

const typeKey = (type: ConnectProto.DriverIncidentType) =>
  getEnumName(ConnectProto.DriverIncidentTypeSchema, type);

/** The dispatcher's reason for the job, under its status. */
export const JobIncidentSelect: React.FC<Props> = ({
  current,
  staged,
  onStage,
  notCompleted,
  disabled,
}) => {
  const t = useT();
  const options = incidentOptions(notCompleted);
  // The pick reads as the job's note would; debt is EXPIRED
  const shown: JobIncidentNote | undefined = staged
    ? staged.type === EXPIRED
      ? { kind: "debt" }
      : { kind: "incident", type: staged.type }
    : current;
  const type = staged?.type ?? (current?.kind === "debt" ? EXPIRED : current?.type);
  const explanation = staged?.explanation ?? current?.explanation ?? "";

  return (
    <div className="flex flex-col gap-1.5">
      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid="job-incident-select"
          aria-label={t(Namespace.PhotoReport, "incident")}
          disabled={disabled}
          className="max-w-full self-start rounded-sm disabled:opacity-60"
        >
          <IncidentChip
            tone={shown?.kind ?? "none"}
            title={explanation || undefined}
            className="flex items-center gap-0.5"
          >
            <span className="truncate">
              {(shown && incidentNoteLabel(t, shown)) ?? t(Namespace.PhotoReport, "incidentNone")}
            </span>
            <IcChevronDownThin className="-mr-1 size-4 shrink-0" />
          </IncidentChip>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            value={String(type ?? "")}
            onValueChange={(value) => {
              const next = options.find((option) => option === Number(value));
              // A new type keeps the words typed so far
              if (next !== undefined) onStage({ type: next, explanation });
            }}
          >
            {options.map((option) => (
              <DropdownMenuRadioItem
                key={option}
                value={String(option)}
                data-testid={`job-incident-option-${typeKey(option)}`}
              >
                {t(Namespace.Enum, typeKey(option)!)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Figma: the field opens for «Другое», or to show words already given */}
      {(type === OTHER || explanation !== "") && (
        <Input
          data-testid="job-incident-explanation"
          value={explanation}
          onChange={(event) =>
            type !== undefined && onStage({ type, explanation: event.target.value })
          }
          placeholder={t(
            Namespace.PhotoReport,
            type === undefined ? "incidentExplanationPickFirst" : "incidentExplanation",
          )}
          maxLength={EXPLANATION_MAX_LENGTH}
          disabled={disabled || type === undefined}
          className="!h-8 text-xs"
        />
      )}
    </div>
  );
};
